import { Block } from "@/types/editor/blocks";
import { characterShapes, Characters } from "@/config/font";
import { TransformHandle } from "./types";

// Pattern dimensions in mm (from pattern.tsx)
export const PATTERN_WIDTH_MM = 7.857 / 2; // Halved for more repetitions
export const PATTERN_HEIGHT_MM = 4.267 / 2; // Halved for more repetitions

// Add function to calculate exact pattern repetitions
export const calculateExactPatternRepetitions = (
  widthMM: number,
  heightMM: number
) => {
  // Calculate exact number of patterns that should fit
  const exactPatternsX = widthMM / PATTERN_WIDTH_MM;
  const exactPatternsY = heightMM / PATTERN_HEIGHT_MM;

  return {
    exact: {
      x: exactPatternsX,
      y: exactPatternsY,
    },
    // For visual rendering, we need to ceil to cover the entire area
    ceil: {
      x: Math.ceil(exactPatternsX),
      y: Math.ceil(exactPatternsY),
    },
    // Calculate pattern size needed for exact repetition
    patternSize: {
      widthMM: widthMM / exactPatternsX, // This will be the same as PATTERN_WIDTH_MM
      heightMM: heightMM / exactPatternsY, // This will be the same as PATTERN_HEIGHT_MM
    },
  };
};

// Function to find which block was clicked
export const findClickedBlock = (
  x: number,
  y: number,
  block: Block,
  calculateRectFn: () => {
    x: number;
    y: number;
    width: number;
    height: number;
  },
  getBlockBoundsFn: (
    block: Block,
    path: number[],
    parentBounds: { x: number; y: number; width: number; height: number }
  ) => { x: number; y: number; width: number; height: number },
  path: number[] = [],
  parentBounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null = null
): number[] | null => {
  const bounds =
    path.length === 0
      ? calculateRectFn()
      : getBlockBoundsFn(block, path, parentBounds!);

  // Check if click is within this block's bounds
  if (
    x >= bounds.x &&
    x <= bounds.x + bounds.width &&
    y >= bounds.y &&
    y <= bounds.y + bounds.height
  ) {
    // If this block has children, check them first (in reverse to check front-to-back)
    if (block.blocks && block.blocks.length > 0) {
      for (let i = block.blocks.length - 1; i >= 0; i--) {
        const childPath = [...path, i];
        const result = findClickedBlock(
          x,
          y,
          block.blocks[i],
          calculateRectFn,
          getBlockBoundsFn,
          childPath,
          bounds
        );
        if (result) return result;
      }
    }
    // If no child was clicked or this is a leaf block, return this block's path
    return path;
  }
  return null;
};

export const getBlockBounds = (
  block: Block,
  path: number[],
  parentBounds: { x: number; y: number; width: number; height: number },
  blocks: Block[]
) => {
  const isVertical = parentBounds.width < parentBounds.height;
  const parent = path.slice(0, -1).reduce((currentBlock, index) => {
    return currentBlock.blocks?.[index] as Block;
  }, blocks[0]);
  const index = path[path.length - 1];

  if (block.isContainer) {
    const containerWidth = (block.width / parent.width) * parentBounds.width;
    const containerHeight =
      (block.height / parent.height) * parentBounds.height;

    const parentBlocks = parent.blocks!;
    const allocatedWidth = isVertical
      ? parentBounds.width
      : parentBounds.width / parentBlocks.length;
    const allocatedHeight = isVertical
      ? parentBounds.height / parentBlocks.length
      : parentBounds.height;
    const allocatedX = isVertical
      ? parentBounds.x
      : parentBounds.x + index * allocatedWidth;
    const allocatedY = isVertical
      ? parentBounds.y + index * allocatedHeight
      : parentBounds.y;

    return {
      x: allocatedX + (allocatedWidth - containerWidth) / 2,
      y: allocatedY + (allocatedHeight - containerHeight) / 2,
      width: containerWidth,
      height: containerHeight,
    };
  } else {
    const nonContainerBlocks = parent.blocks!.filter(
      (b: Block) => !b.isContainer
    );
    const nonContainerIndex = parent
      .blocks!.slice(0, index)
      .filter((b: Block) => !b.isContainer).length;
    const availableSpace = isVertical
      ? parentBounds.height
      : parentBounds.width;
    const blockSize = availableSpace / nonContainerBlocks.length;

    return {
      x: isVertical
        ? parentBounds.x
        : parentBounds.x + nonContainerIndex * blockSize,
      y: isVertical
        ? parentBounds.y + nonContainerIndex * blockSize
        : parentBounds.y,
      width: isVertical ? parentBounds.width : blockSize,
      height: isVertical ? blockSize : parentBounds.height,
    };
  }
};

export const calculateRect = (
  width: number,
  height: number,
  containerSize: number,
  PADDING: number,
  SCALE: number,
  insetWidth?: number,
  insetHeight?: number,
  insetX?: number,
  insetY?: number,
  overlayImageUrl?: string,
  overlayWidth?: number,
  overlayHeight?: number,
  backgroundImageUrl?: string,
  backgroundWidth?: number,
  backgroundHeight?: number,
  characterWidth?: number,
  characterHeight?: number
) => {
  // Calculate the width and height in pixels
  const initialPixelWidth = width * SCALE;
  const initialPixelHeight = height * SCALE;

  // If we have character dimensions, use those instead
  const pixelWidth = characterWidth
    ? characterWidth * SCALE
    : initialPixelWidth;
  const pixelHeight = characterHeight
    ? characterHeight * SCALE
    : initialPixelHeight;

  // If we have inset dimensions and overlay dimensions are provided, calculate the scale
  if (insetWidth && insetHeight && (overlayImageUrl || backgroundImageUrl)) {
    // Calculate the scale ratio between physical dimensions and canvas size
    const canvasSize = containerSize - PADDING * 2;

    // Determine which image to use for calculations (prioritize overlay)
    const useOverlay = !!overlayImageUrl;
    const imageWidth = useOverlay ? overlayWidth : backgroundWidth;
    const imageHeight = useOverlay ? overlayHeight : backgroundHeight;

    // Get image dimensions in pixels
    const imagePixelWidth = imageWidth || 1080;
    const imagePixelHeight = imageHeight || 1080;

    // Calculate the position of the image
    const imageX_pos = PADDING;
    const imageY_pos = PADDING;

    // Calculate scale to fit image in canvas
    const imageScale = canvasSize / imagePixelWidth;

    // Calculate the scaled image dimensions
    const scaledImageWidth = imagePixelWidth * imageScale;
    const scaledImageHeight = imagePixelHeight * imageScale;

    // Calculate the inset position relative to the scaled image
    // Use the exact pixel values provided for inset if available
    const insetRatioX = insetX! / imagePixelWidth;
    const insetRatioY = insetY! / imagePixelHeight;
    const insetRatioWidth = insetWidth / imagePixelWidth;
    const insetRatioHeight = insetHeight / imagePixelHeight;

    // Apply the ratios to get the actual pixel positions
    // Add image position offsets if provided
    // Round up all pixel values to ensure clean integer values
    const scaledInsetX = Math.ceil(imageX_pos + scaledImageWidth * insetRatioX);
    const scaledInsetY = Math.ceil(
      imageY_pos + scaledImageHeight * insetRatioY
    );
    const scaledInsetWidth = Math.ceil(scaledImageWidth * insetRatioWidth);
    const scaledInsetHeight = Math.ceil(scaledImageHeight * insetRatioHeight);

    // Always stretch to fill the inset area completely, even when exporting
    const scaledDesignWidth = scaledInsetWidth;
    const scaledDesignHeight = scaledInsetHeight;

    // Position at the inset coordinates
    const x = scaledInsetX;
    const y = scaledInsetY;

    // Return the final dimensions with all values rounded up
    return {
      x: Math.ceil(x),
      y: Math.ceil(y),
      width: Math.ceil(scaledDesignWidth),
      height: Math.ceil(scaledDesignHeight),
    };
  }

  // Default positioning if no inset values
  const availableSpace = containerSize - PADDING * 2;
  const scaleFactorWidth = availableSpace / pixelWidth;
  const scaleFactorHeight = availableSpace / pixelHeight;
  const scaleFactor = Math.min(scaleFactorWidth, scaleFactorHeight);
  const scaledWidth = pixelWidth * scaleFactor;
  const scaledHeight = pixelHeight * scaleFactor;
  const x = (containerSize - scaledWidth) / 2;
  const y = (containerSize - scaledHeight) / 2;

  return { x, y, width: scaledWidth, height: scaledHeight };
};

// Convert screen coordinates to canvas coordinates
export const convertToCanvasCoords = (
  screenX: number,
  screenY: number,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  containerSize: number
) => {
  if (!canvasRef.current) return { x: screenX, y: screenY };
  const rect = canvasRef.current.getBoundingClientRect();
  const scale = rect.width / containerSize;

  return {
    x: (screenX - rect.left) / scale,
    y: (screenY - rect.top) / scale,
  };
};

// Add function to calculate natural width ratio
export const getNaturalWidthRatio = (character: string): number => {
  if (!character) return 1;
  const char = character.toLowerCase();
  if (char === " ") return 0.5; // Space is half width
  if (char === ".") return 0.2; // Dot is very narrow
  if (char === "-") return 0.5; // Dash is half width

  const shape = characterShapes[char as Characters];
  if (!shape) return 1;

  const [, , vbWidth, vbHeight] = shape.viewBox.split(" ").map(Number);
  return vbWidth / vbHeight;
};

export const getHandles = (rect: {
  x: number;
  y: number;
  width: number;
  height: number;
}): TransformHandle[] => {
  const { x, y, width, height } = rect;
  return [
    // Edge handles
    {
      x: x + width,
      y: y + height / 2,
      cursor: "e-resize",
      action: "e",
    },
    {
      x: x,
      y: y + height / 2,
      cursor: "w-resize",
      action: "w",
    },
    {
      x: x + width / 2,
      y: y,
      cursor: "n-resize",
      action: "n",
    },
    {
      x: x + width / 2,
      y: y + height,
      cursor: "s-resize",
      action: "s",
    },
    // Corner handles
    {
      x: x + width,
      y: y,
      cursor: "ne-resize",
      action: "ne",
    },
    {
      x: x,
      y: y,
      cursor: "nw-resize",
      action: "nw",
    },
    {
      x: x + width,
      y: y + height,
      cursor: "se-resize",
      action: "se",
    },
    {
      x: x,
      y: y + height,
      cursor: "sw-resize",
      action: "sw",
    },
  ];
};

export const getSelectedBlockBounds = (
  selectedPath: number[],
  blocks: Block[],
  calculateRect: () => { x: number; y: number; width: number; height: number }
) => {
  if (!selectedPath || selectedPath.length === 0 || !blocks) {
    return calculateRect();
  }

  // Get the root block
  const rootBlock = blocks[0];
  if (!rootBlock) return calculateRect();

  // Calculate the initial rect dimensions
  const rect = calculateRect();
  const scaleX = rect.width / rootBlock.width;
  const scaleY = rect.height / rootBlock.height;

  // If we're selecting the root block (Container 0), return its bounds
  if (selectedPath.length === 1 && selectedPath[0] === 0) {
    return rect;
  }

  // Start with root block position
  let x = rect.x;
  let y = rect.y;
  let width = scaleX * rootBlock.width;
  let height = scaleY * rootBlock.height;

  // Navigate through the path to find the selected block
  let currentBlock = rootBlock;
  // Skip the first path index since we're already at the root block
  for (let i = 1; i < selectedPath.length; i++) {
    if (!currentBlock.blocks) break;

    const isVertical = currentBlock.width < currentBlock.height;
    const nextBlock = currentBlock.blocks[selectedPath[i]];
    if (!nextBlock) break;

    // For containers, calculate center position
    if (nextBlock.isContainer) {
      const containerWidth = (nextBlock.width / currentBlock.width) * width;
      const containerHeight = (nextBlock.height / currentBlock.height) * height;

      // Calculate allocated space
      const blocks = currentBlock.blocks;
      const allocatedWidth = isVertical ? width : width / blocks.length;
      const allocatedHeight = isVertical ? height / blocks.length : height;
      const allocatedX = isVertical ? x : x + selectedPath[i] * allocatedWidth;
      const allocatedY = isVertical ? y + selectedPath[i] * allocatedHeight : y;

      // Center the container in its allocated space
      x = allocatedX + (allocatedWidth - containerWidth) / 2;
      y = allocatedY + (allocatedHeight - containerHeight) / 2;
      width = containerWidth;
      height = containerHeight;
    } else {
      // For character blocks, calculate position based on non-container siblings
      const nonContainerBlocks = currentBlock.blocks.filter(
        (b) => !b.isContainer
      );
      const nonContainerIndex = currentBlock.blocks
        .slice(0, selectedPath[i])
        .filter((b) => !b.isContainer).length;
      const availableSpace = isVertical ? height : width;
      const blockSize = availableSpace / nonContainerBlocks.length;

      if (isVertical) {
        y += nonContainerIndex * blockSize;
        height = blockSize;
      } else {
        x += nonContainerIndex * blockSize;
        width = blockSize;
      }
    }

    currentBlock = nextBlock;
  }

  return { x, y, width, height };
};
